import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { Slider } from "@/components/site/BeforeAfter";
import { RequestButton, WhatsAppButton } from "@/components/site/Buttons";
import { usePageMeta } from "@/lib/usePageMeta";
import { CASE_STUDIES, CASE_CLOSING } from "@/content/site";
import ComingSoon from "@/pages/ComingSoon";

const Step = ({ s, i }) => (
  <article className="py-14 md:py-20 border-b border-[var(--line)] grid lg:grid-cols-12 gap-8 lg:gap-14 items-center" data-testid={`case-step-${i + 1}`}>
    <Reveal className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
      <div className="aspect-square overflow-hidden chamfer border border-[var(--line)]">
        <img src={s.image?.src} alt={s.image?.alt} loading="lazy" className="h-full w-full object-cover" />
      </div>
    </Reveal>
    <Reveal className="lg:col-span-6" delay={0.08}>
      <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mute)]">
        <span className="text-[var(--red)]">Step 0{i + 1}</span><span className="h-px w-8 bg-[var(--red)]" aria-hidden />{s.label}
      </p>
      <h2 className="mt-5 font-display font-black uppercase text-5xl md:text-6xl leading-[0.9]">{s.title}</h2>
      <p className="mt-6 text-base md:text-lg text-[var(--grey)] leading-relaxed max-w-xl">{s.text}</p>
    </Reveal>
  </article>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const c = CASE_STUDIES.find((x) => x.slug === slug);
  usePageMeta(
    c ? `Case Study #${c.number}: ${c.tool} ${c.title} | NE Tool Repair` : "Case study not found | NE Tool Repair",
    c ? `${c.tool} ${c.title.toLowerCase()} by NE Tool Repair, Newcastle. ${c.process.join(", ")}.` : "",
    `/case-studies/${slug}`
  );
  if (!c) return <ComingSoon title="Case study not found" />;
  return (
    <div data-testid="case-study-page">
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div className="absolute inset-0 grid-lines [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-10 md:pt-16 pb-14 md:pb-20">
          <Link to="/case-studies" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-[var(--grey)] hover:text-white transition-colors" data-testid="case-back-link">
            <ArrowLeft className="h-4 w-4" aria-hidden /> All case studies
          </Link>
          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-end">
            <Reveal className="lg:col-span-8">
              <Eyebrow>Case study #{c.number} · {c.tool}</Eyebrow>
              <h1 className="mt-6 font-display font-black uppercase text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.86]" data-testid="case-title">{c.title}</h1>
            </Reveal>
            <Reveal className="lg:col-span-4" delay={0.1}>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">Repair process</p>
              <ol className="mt-3 flex flex-wrap gap-2" data-testid="case-process">
                {c.process.map((p, i) => (
                  <li key={p} className="flex items-center gap-2 border border-[var(--line)] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.16em]"><span className="text-[var(--red)]">0{i + 1}</span>{p}</li>
                ))}
              </ol>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 border-b border-[var(--line)] bg-[var(--char)]" data-testid="case-before-after">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="lg:col-span-7"><Slider data={c.beforeAfter} /></Reveal>
          <Reveal className="lg:col-span-5" delay={0.08}>
            <Eyebrow>The result</Eyebrow>
            <p className="mt-6 text-base md:text-lg text-[var(--grey)] leading-relaxed">{c.summary}</p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">Drag the slider to compare</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10">
        {c.steps.map((s, i) => <Step key={s.title} s={s} i={i} />)}
      </section>

      <section className="py-20 md:py-28 border-b border-[var(--line)]" data-testid="case-closing">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">
              {CASE_CLOSING.tagline.map((t, i) => <span key={t} className={`block ${i === 1 ? "text-[var(--red)]" : i === 2 ? "stroke-text" : ""}`}>{t}</span>)}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <p className="text-base md:text-lg text-[var(--grey)] leading-relaxed">{CASE_CLOSING.text}</p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mute)]">NE Tool Repair — Newcastle • North East</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <RequestButton testId="case-request-btn" />
              <WhatsAppButton testId="case-whatsapp-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
